import React, { useEffect, useState } from 'react';

import {
  Box,
  Button,
  CircularProgress,
  Hidden,
  Slider,
  Typography
} from '@mui/material';

import { styled } from '@mui/material/styles';

import { Circle, GoogleApiWrapper, Map } from 'google-maps-react';

import LocationSearch from './LocationSearch';

import gaEvent from '../../lib/gaEvent';
import axiosInstance from '../../lib/axiosInstance';


const containerStyle = {
  position: 'relative',
  width: '100%',
  height: 400,
  pointerEvents: 'none',
};


const HighlightText = styled(Typography)(({ theme }) => ({
  color: theme.palette.secondary.main,
  fontWeight: 500,
}))


const Where = ({ google, make, service, handleNextStep, activeStep, where, setWhere }) => {
  
  const [businesses, setBusinesses] = useState([]);
  const [loadingBusinesses, setLoadingBusinesses] = useState(false);

  const selectWhere = () => {
    gaEvent({
      category: 'Request Quotes',
      action: 'Submit Location',
      label: 'Choose Location',
    });

    handleNextStep(4);
  }

  useEffect(() => {
    // change to use coords
    if (!where.address) {
      setBusinesses(null);
      return;
    }
    setLoadingBusinesses(true);
    const fetchData = async () => {
      const result = await axiosInstance(`/content/onboarding-business/`, {
        params: {
          lat: where.center.lat,
          lng: where.center.lng,
          make,
          service,
          distance: where.distance,
        },
      });
      setBusinesses(result.data);
      setLoadingBusinesses(false);
    };

    fetchData();
  }, [where.address, where.distance]);

  const zoom =
    where.distance >= 70
      ? 8
      : where.distance >= 50
      ? 8.5
      : where.distance >= 40
      ? 9
      : where.distance >= 30
      ? 9.5
      : where.distance >= 20
      ? 10
      : where.distance >= 15
      ? 10.5
      : where.distance >= 10
      ? 11
      : where.distance >= 5
      ? 12
      : 13;

  const zoomMobile =
    where.distance >= 80
      ? 7.5
      : where.distance >= 60
      ? 8
      : where.distance >= 50
      ? 8.5
      : where.distance >= 45
      ? 8.8
      : where.distance >= 40
      ? 9
      : where.distance >= 35
      ? 9.2
      : where.distance >= 30
      ? 9.4
      : where.distance >= 25
      ? 9.6
      : where.distance >= 20
      ? 10
      : where.distance >= 15
      ? 10.4
      : where.distance >= 10
      ? 10.9
      : where.distance >= 5
      ? 11.9
      : 12;

  const valuetext = value => {
    return `${value}km`;
  };

  return <>
    {activeStep === 3 && (
      <Box
        sx={{
          display: {
            xs: 'flex',
            md: 'none',
          },
          height: 'calc(100vh - 56px - 48px)',
          flexDirection: 'column',
          width: '100vw',
          px: {
            xs: 1,
            md: 0
          }
        }}
      >
        {!where.address ? (
          <Box width="100%">
            <LocationSearch setWhere={setWhere} initialAddress={where.address} />
          </Box>
        ) : (
          <>
            <Box
              flex={1}
              mt={-3}
              pt={1}
              bgcolor="#fff"
              display="flex"
              flexDirection="column"
            >
              <Box
                width="100%"
                minHeight={52}
                height={52}
                maxHeight={52}
                mb={1}
                px={1}
                textAlign="center"
                display="flex"
                justifyContent="center"
                alignItems="center"
              >
                {!loadingBusinesses && businesses ? (
                  <>
                    <Typography
                      sx={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      <Typography
                        component="span"
                        sx={{
                          color: 'primary.light',
                          fontWeight: 500,
                          fontSize: 18,
                        }}
                      >
                        {businesses.count}
                      </Typography>{' '}
                      relevant mechanics within{' '}
                      <HighlightText component="span">
                        {where.distance}km
                      </HighlightText>
                      <br />
                      from{' '}
                      <HighlightText component="span">
                        {where.address}
                      </HighlightText>
                    </Typography>
                  </>
                ) : (
                  <Box display="flex" alignItems="center" justifyContent="center">
                    <Typography>Searching</Typography>
                    <Box ml={2} display="flex">
                      <CircularProgress size={16} />
                    </Box>
                  </Box>
                )}
              </Box>
              <Box flex={1} position="relative">
                <Map
                  google={google}
                  initialCenter={where.center}
                  center={where.center}
                  zoom={zoomMobile}
                  containerStyle={{ ...containerStyle, height: '100%' }}
                  disableDefaultUI={true}
                  draggable={false}
                >
                  <Circle
                    center={where.center}
                    radius={where.distance * 1000}
                    strokeOpacity={0}
                  />
                </Map>
                {businesses?.count === 0 && !loadingBusinesses && (
                  <Box
                    bgcolor="rgba(0, 0, 0, 0.5)"
                    color="#fff"
                    position="absolute"
                    top={0}
                    width="100%"
                    height="100%"
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                  >
                    {where.distance === 100 ? (
                      <Typography variant="h4">No mechanics found nearby :(</Typography>
                    ) : (
                      <Typography variant="h4">Drag to increase search area</Typography>
                    )}
                  </Box>
                )}
              </Box>
              <Box
                sx={{
                  py: 1,
                  px: 4
                }}
              >
                <Slider
                  value={where.distance}
                  min={5}
                  step={5}
                  max={100}
                  getAriaValueText={valuetext}
                  valueLabelFormat={valuetext}
                  onChange={(_event, newValue) =>
                    setWhere(prevState => ({
                      ...prevState,
                      distance: newValue,
                    }))
                  }
                  valueLabelDisplay="on"
                  color="secondary"
                />
              </Box>
            </Box>
            <Box
              mt={2}
              textAlign="center"
              display="flex"
              justifyContent="center"
              alignItems="center"
            >
              <Box mr={2} color="primary.light">
                <Button
                  variant="text"
                  color="inherit"
                  onClick={() =>
                    setWhere({
                      center: {
                        lat: -27,
                        lng: 25,
                      },
                      address: null,
                      fullAddress: null,
                      distance: 15,
                    })
                  }
                  disabled={loadingBusinesses}
                >
                  Change address
                </Button>
              </Box>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => selectWhere()}
                disabled={loadingBusinesses || !businesses?.count}
              >
                Continue
              </Button>
            </Box>
          </>
        )}
      </Box>
    )}
    <Hidden mdDown>
      <Box width={500}>
        {activeStep === 2 && (
          <LocationSearch
            setWhere={setWhere}
            handleNextStep={handleNextStep}
            initialAddress={where.address}
            isLight
            activeStep={activeStep}
          />
        )}
        {activeStep === 3 && (
          <>
            <LocationSearch setWhere={setWhere} initialAddress={where.address} />
            <Box mt={2} p={1} pb={2} bgcolor="#fff" boxShadow={2}>
              <Map
                google={google}
                initialCenter={where.center}
                center={where.center}
                zoom={zoom}
                containerStyle={containerStyle}
                disableDefaultUI={true}
                draggable={false}
              >
                <Circle center={where.center} radius={where.distance * 1000} strokeOpacity={0} />
              </Map>
              <Slider
                value={where.distance}
                min={5}
                step={5}
                max={100}
                getAriaValueText={valuetext}
                valueLabelFormat={valuetext}
                onChange={(_event, newValue) =>
                  setWhere(prevState => ({
                    ...prevState,
                    distance: newValue,
                  }))
                }
                valueLabelDisplay="on"
                color="secondary"
              />
              <Box width="100%" minHeight={28} mt={1} textAlign="center">
                {!loadingBusinesses && businesses ? (
                  <>
                    <Typography>
                      <Typography
                        component="span"
                        sx={{
                          color: 'primary.light',
                          fontWeight: 500,
                          fontSize: 18,
                        }}
                      >
                        {businesses.count}
                      </Typography>{' '}
                      relevant mechanics within{' '}
                      <HighlightText component="span">
                        {where.distance}km
                      </HighlightText>{' '}
                      from{' '}
                      <HighlightText component="span">
                        {where.address}
                      </HighlightText>
                    </Typography>
                    {businesses.count === 0 && where.distance !== 100 && (
                      <Typography variant="h6">Drag to increase search area</Typography>
                    )}
                    {businesses.count === 0 && where.distance === 100 && (
                      <Typography variant="h6">No mechanics found nearby :(</Typography>
                    )}
                  </>
                ) : (
                  <Box display="flex" alignItems="center" justifyContent="center">
                    <Typography>Searching</Typography>
                    <Box ml={2}>
                      <CircularProgress size={16} />
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
            <Box mt={2} textAlign="center">
              <Button
                variant="contained"
                color="secondary"
                size="large"
                onClick={() => selectWhere()}
                disabled={loadingBusinesses || !businesses?.count}
              >
                Continue
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Hidden>
  </>;
};

const LoadingContainer = () => (
  <Box display="flex" alignItems="center" justifyContent="center">
    <Hidden mdDown>
      <CircularProgress size={24} />
    </Hidden>
  </Box>
);

export default GoogleApiWrapper({
  apiKey: 'AIzaSyDoOHc6XRVaQso0d8EybI4-xaAv965tCXA',
  LoadingContainer: LoadingContainer,
})(Where);
